import AddIcon from "@mui/icons-material/Add";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Divider, IconButton, InputAdornment, type SxProps } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { DaysInWeek, DaysInWeekSelector } from "@/components/RecurringShift/util/DaysInWeekSelector";
import { useJobColors } from "@/hooks/useJobColors";
import { STATUS } from "@/theme/tokens";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import { RoleChips } from "@/ui/molecules/RoleChips";
import { NEUTRAL_CATEGORICAL_COLOR } from "@/utils/categoricalColors";
import { censusBoundLabel } from "@/utils/censusResolution";
import {
	type RatioLadderRowProps,
	V1_DAYS_PICKER_GAP,
	V1_ERROR_OUTLINE_PADDING,
	V1_ERROR_OUTLINE_RADIUS,
	V1_JOB_DOT_SIZE,
	V1_LADDER_COMPACT_FIELD_WIDTH,
	V1_LADDER_MAX_FIELD_WIDTH,
} from "./consts";
import { rowDays } from "./ratioLadderCoverage";

const compactNumericFieldSx: SxProps = { width: V1_LADDER_COMPACT_FIELD_WIDTH } as const;
/** Wider than its siblings: it also holds the "no upper limit" button. */
const maxFieldSx: SxProps = { width: V1_LADDER_MAX_FIELD_WIDTH } as const;

const dashedDividerSx: SxProps = { borderStyle: "dashed" } as const;

/** One ratio-ladder rung in the Schedule Template popup: job or role chips, Min / Max / Amount, and its days. */
export const RatioLadderRowV1: FC<RatioLadderRowProps> = ({
	ratio,
	locationRoles,
	showRoleChips,
	showJobChips,
	jobDisplayName,
	hasRolesError,
	hasJobsError,
	hasAmountError,
	hasDaysError,
	hasRangeError,
	hasCensusConflict,
	conflictingDays,
	isOpenEnded,
	autoFocusMax,
	showAddButton,
	showDivider,
	onUpdate,
	onMinTyped,
	onAmountTyped,
	onMaxTyped,
	onMaxUnbounded,
	onMaxFocus,
	onMaxBlur,
	onDelete,
	onAdd,
}) => {
	const jobColor = useJobColors();
	const { t } = useTranslation();
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 0.5,
				width: "100%",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				{showJobChips && (
					<Box
						sx={{
							display: "flex",
							gap: 1,
							alignItems: "center",
							...(hasJobsError ? { outline: `1px solid ${STATUS.ERROR.main}`, borderRadius: V1_ERROR_OUTLINE_RADIUS, p: V1_ERROR_OUTLINE_PADDING } : {}),
						}}
					>
						<Box
							sx={{
								// DEFAULT: a rung with no job gets the neutral colour, as main's editor paints it.
								bgcolor: `${ratio.jobId ? jobColor(ratio.jobId).dot : NEUTRAL_CATEGORICAL_COLOR.dot} !important`,
								borderRadius: "50%",
								width: V1_JOB_DOT_SIZE,
								height: V1_JOB_DOT_SIZE,
							}}
						/>
						{jobDisplayName}
					</Box>
				)}
				{showRoleChips && (
					<Box
						sx={hasRolesError ? { outline: `1px solid ${STATUS.ERROR.main}`, borderRadius: V1_ERROR_OUTLINE_RADIUS, p: V1_ERROR_OUTLINE_PADDING } : undefined}
					>
						<RoleChips
							hideAllOption
							availableRoles={locationRoles}
							onSelectedRoleFiltersChanged={(selectedRoles) => {
								onUpdate({
									roles: selectedRoles.filter((r) => r !== "all"),
								});
							}}
							initiallySelected={ratio.roles}
						/>
					</Box>
				)}
				<SystemValidationTextField
					inputType={"number"}
					label={t("schedule.template.ratio.ladder.min")}
					value={ratio.censusStart.toString()}
					onChange={(event) => onMinTyped(event.target.value)}
					inputProps={{ inputProps: { min: 0 } }}
					inputSx={compactNumericFieldSx}
					reserveHeightForHelperText={false}
					defaultErrorColor={hasCensusConflict || hasRangeError ? STATUS.ERROR.main : undefined}
					defaultBorderColor={hasCensusConflict || hasRangeError ? STATUS.ERROR.main : undefined}
				/>
				<SystemValidationTextField
					inputType={"text"}
					label={t("schedule.template.ratio.ladder.max")}
					value={Number.isNaN(ratio.censusEnd) ? "" : censusBoundLabel(ratio.censusEnd)}
					onChange={(event) => onMaxTyped(event.target.value)}
					inputProps={{
						inputProps: { inputMode: "numeric" },
						autoFocus: autoFocusMax,
						onFocus: onMaxFocus,
						onBlur: onMaxBlur,
						endAdornment: (
							<InputAdornment position="end">
								<InHouseTooltip title={t("schedule.template.ratio.ladder.max.unbounded")}>
									<IconButton
										size="small"
										edge="end"
										aria-label={t("schedule.template.ratio.ladder.max.unbounded")}
										aria-pressed={isOpenEnded}
										color={isOpenEnded ? "primary" : "default"}
										onClick={onMaxUnbounded}
										data-testid={`ratio-ladder-max-unbounded-button-${ratio.id}`}
									>
										<AllInclusiveIcon fontSize="inherit" />
									</IconButton>
								</InHouseTooltip>
							</InputAdornment>
						),
					}}
					inputSx={maxFieldSx}
					reserveHeightForHelperText={false}
					defaultErrorColor={hasCensusConflict || hasRangeError ? STATUS.ERROR.main : undefined}
					defaultBorderColor={hasCensusConflict || hasRangeError ? STATUS.ERROR.main : undefined}
					dataTestId={`ratio-ladder-max-input-${ratio.id}`}
				/>
				<SystemValidationTextField
					inputType={"number"}
					label={t("amount")}
					value={ratio.amount.toString()}
					onChange={(event) => onAmountTyped(event.target.value)}
					inputProps={{ inputProps: { min: 0 } }}
					inputSx={compactNumericFieldSx}
					reserveHeightForHelperText={false}
					defaultErrorColor={hasAmountError ? STATUS.ERROR.main : undefined}
					defaultBorderColor={hasAmountError ? STATUS.ERROR.main : undefined}
				/>
				<IconButton size="small" aria-label={t("schedule.template.ratio.ladder.row.remove")} onClick={onDelete}>
					<DeleteIcon fontSize="small" />
				</IconButton>
				{showAddButton && (
					<IconButton size="small" aria-label={t("schedule.template.ratio.ladder.row.add")} onClick={onAdd}>
						<AddIcon fontSize="small" />
					</IconButton>
				)}
			</Box>
			<Box
				sx={
					hasDaysError
						? { outline: `1px solid ${STATUS.ERROR.main}`, borderRadius: V1_ERROR_OUTLINE_RADIUS, p: V1_ERROR_OUTLINE_PADDING, width: "fit-content" }
						: undefined
				}
			>
				<DaysInWeekSelector
					size="small"
					gap={V1_DAYS_PICKER_GAP}
					selectedDays={Object.values(DaysInWeek).filter((fullDay) => rowDays(ratio).some((short) => fullDay.toLowerCase().startsWith(short)))}
					onChange={(selectedDays) =>
						onUpdate({
							days: selectedDays.map((d) => d.toLowerCase().slice(0, 3)),
						})
					}
					errorDays={conflictingDays ? Object.values(DaysInWeek).filter((fullDay) => conflictingDays.has(fullDay.toLowerCase().slice(0, 3))) : undefined}
				/>
			</Box>
			{showDivider && <Divider sx={dashedDividerSx} />}
		</Box>
	);
};
